<template>
  <div class="conversation-list">
    <!-- 头部 -->
    <div class="conversation-list-header">
      <h2>对话列表</h2>
      <div class="header-actions">
        <!-- 客服状态筛选器 -->
        <el-dropdown trigger="click" @command="handleStatusChange" class="filter-dropdown-header">
          <el-button 
            type="primary"
            plain
            size="small"
            class="status-dropdown-btn"
          >
            <i class="filter-icon" :class="staffStatus + '-dot'"></i>
            <span class="status-label">{{ statusLabels[staffStatus] }}</span>
            <i class="el-icon-arrow-down"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown" class="filter-menu-header">
            <el-dropdown-item command="online">
              <i class="filter-icon online-dot"></i>
              <span class="filter-label-text">上线</span>
            </el-dropdown-item>
            <el-dropdown-item command="busy">
              <i class="filter-icon busy-dot"></i>
              <span class="filter-label-text">忙碌</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <!-- 全局音量开关 -->
        <el-tooltip :content="globalSoundEnabled ? '关闭声音提醒' : '开启声音提醒'" placement="top">
          <el-button 
            type="text"
            :icon="globalSoundEnabled ? 'el-icon-service' : 'el-icon-service'"
            @click="toggleGlobalSound"
            title="声音提醒"
            class="sound-button"
            :style="{ color: globalSoundEnabled ? '#409eff' : '#c0c4cc' }"
          >
            <i class="volume-icon" :class="globalSoundEnabled ? 'icon-volume' : 'icon-volume-mute'"></i>
          </el-button>
        </el-tooltip>
        
        <el-button
          type="text"
          icon="el-icon-refresh"
          :loading="refreshing"
          @click="refreshConversations"
          title="刷新列表"
        />
      </div>
    </div>

    <!-- 全局搜索框 -->
    <div class="global-search-container">
      <!-- 搜索类型选择器 - 第一行 -->
      <div class="search-type-row">
        <span class="search-type-label">搜索类型:</span>
        <el-radio-group
          v-model="searchType"
          @change="clearGlobalSearch"
          class="search-type-radio-group"
        >
          <el-radio-button label="message">聊天记录</el-radio-button>
          <el-radio-button label="user">用户信息</el-radio-button>
        </el-radio-group>
      </div>
      
      <!-- 搜索输入框 - 第二行 -->
      <div class="search-input-row">
        <el-input
          v-model="globalSearchKeyword"
          :placeholder="searchType === 'message' ? '搜索聊天记录...' : '搜索用户信息...'"
          prefix-icon="el-icon-search"
          clearable
          size="small"
          @keyup.enter.native="performGlobalSearch"
          @clear="clearGlobalSearch"
        >
          <el-button 
            slot="append" 
            icon="el-icon-search"
            :loading="globalSearching"
            @click="performGlobalSearch"
          />
        </el-input>
      </div>
    </div>

    <!-- 全局搜索结果 -->
    <div v-if="showGlobalSearchResults" class="global-search-results">
      <div class="search-results-header">
        <span>搜索结果: {{ globalSearchResults.length }} 条</span>
        <el-button type="text" size="mini" @click="clearGlobalSearch">
          <i class="el-icon-close"></i> 关闭
        </el-button>
      </div>
      <div v-if="globalSearching" class="search-loading">
        <i class="el-icon-loading"></i> 搜索中...
      </div>
      <div v-else-if="globalSearchResults.length === 0" class="no-search-results">
        <el-empty description="没有找到相关记录" :image-size="60" />
      </div>
      <div v-else class="search-results-list">
        <div
          v-for="(result, index) in globalSearchResults"
          :key="index"
          class="search-result-item"
          @click="jumpToSearchResult(result)"
        >
          <!-- 消息搜索结果 -->
          <template v-if="searchType === 'message'">
            <div class="result-header">
              <span class="result-customer-name">{{ result.customerName || result.customerId }}</span>
              <span class="result-time">{{ formatTime(result.createTime) }}</span>
            </div>
            <div class="result-content" v-html="highlightKeyword(result.content, globalSearchKeyword)"></div>
            <div class="result-staff">客服: {{ result.receiverName?result.receiverName:'未知' }}</div>
          </template>
          
          <!-- 用户搜索结果 -->
          <template v-if="searchType === 'user'">
            <div class="result-header">
              <span class="result-customer-name">{{ result.customerName || result.customerId }}</span>
              <span class="result-user-id">ID: {{ result.customerId }}</span>
            </div>
            <div class="result-content">
              <span v-if="result.customerLocation">📍 {{ result.customerLocation }}</span>
              <span v-if="result.customerIp">🌐 {{ result.customerIp }}</span>
            </div>
            <!-- <div class="result-staff">客服: {{ result.customerName?result.customerName:'未知' }}</div> -->
          </template>
        </div>
      </div>
    </div>

    <!-- 对话项列表 -->
    <div class="conversation-items" v-loading="loading">
      <!-- 使用手风琴效果的 Collapse 折叠面板 -->
      <el-collapse
        v-model="activeCollapseItems"
        class="conversation-accordion"
      >
        <el-collapse-item
          v-for="admin in adminList"
          :key="admin.staffId"
          :name="admin.staffId"
          class="admin-collapse-item"
        >
          <!-- 客服头部 -->
          <template slot="title">
            <div class="accordion-header">
              <!-- 在线状态 -->
              <div class="status-dot" :class="admin.isOnline ? 'online' : 'offline'"></div>
              
              <!-- 客服名称和客户数 -->
              <div class="staff-info">
                <span class="staff-name">{{ admin.staffName }}</span>
                <el-tag type="success" size="mini" class="customer-count-tag">
                  {{ admin.sessions ? `${admin.sessions.length}个客户` : "无客户" }}
                </el-tag>
              </div>

              <!-- 未读消息总数和NEW徽章 -->
              <div class="admin-unread-section">
                <!-- NEW 闪烁徽章 -->
                <div v-if="getAdminHasNewMessages(admin)" class="new-badge-inline">✦ NEW ✦</div>
                <!-- 未读消息数 -->
                <div class="admin-unread-badge" v-if="admin.staffUnread > 0">
                  {{ admin.staffUnread }}
                </div>
              </div>
            </div>
          </template>
          <!-- 客户列表内容 -->
          <div
            v-if="admin.sessions && admin.sessions.length > 0"
            class="customer-list"
          >
            <div
              v-for="friend in admin.sessions"
              :key="friend.id"
              :class="[
                'conversation-item customer-item',
                {
                  active:
                    getCustomerConversationId(friend, admin.id) ===
                    activeConversationId,
                },
              ]"
              :style="{
                backgroundColor:
                friend.customerName ===
                  seletfriend.customerName
                    ? '#ccd2d9'
                    : 'transparent',
              }"
              @click="selectCustomerConversation(friend, admin)"
            >
              <!-- 头像 -->
              <div class="conversation-avatar">
                <img
                  src="@/assets/images/defaultavatar.png"
                  :alt="friend.peerName"
                />
              </div>
              <!-- 未读消息徽章 -->
              <div
                v-if="getCustomerUnreadCount(friend) > 0"
                class="unread-badge"
              >
                {{ getCustomerUnreadCount(friend) }}
              </div>

              <!-- 客户信息 -->
              <div class="conversation-info">
                <div class="conversation-name" >
                  {{
                    `${
                      friend.customerName
                        ? friend.customerName
                        : friend.customerId
                    }`
                  }}
                  <span class="customer-badge">{{ friend.customerIdentity == 'USER'?'客户':'游客' }}</span>
                  <el-tag
                    :type="friend.onlineStats ? 'success' : 'danger'"
                    size="mini"
                    class="online-status-tag"
                  >
                    {{ friend.onlineStats ? "在线" : "离线" }}
                  </el-tag>
                </div>
                <div class="conversation-last-msg">
                  {{ friend.lastMsgContent || "暂无消息" }}
                </div>
                <div class="conversation-time">
                  {{ formatTime(friend.lastMsgTime) }}
                </div>
              </div>

              <!-- 删除按钮 -->
              <!-- <div class="customer-actions">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-delete"
                  class="delete-customer-btn"
                  @click.stop="confirmDeleteCustomer(friend, admin)"
                  title="删除客户"
                />
              </div> -->
            </div>
          </div>

          <!-- 无客户提示 -->
          <div v-else class="no-customers">
            <el-empty description="暂无客户" :image-size="60" />
          </div>
        </el-collapse-item>
      </el-collapse>

      <!-- 无对话提示 -->
      <div v-if="adminList.length === 0 && !loading" class="no-conversations">
        <el-empty description="暂无对话" />
      </div>
    </div>

    <!-- 新建对话弹窗 -->
    <el-dialog
      title="新建对话"
      :visible.sync="showNewConversationDialog"
      width="400px"
    >
      <el-form
        ref="newConversationForm"
        :model="newConversationForm"
        :rules="newConversationRules"
        label-width="80px"
      >
        <el-form-item label="对话名称" prop="name">
          <el-input
            v-model="newConversationForm.name"
            placeholder="请输入对话名称"
          />
        </el-form-item>
        <el-form-item label="用户标识" prop="token">
          <el-input
            v-model="newConversationForm.token"
            placeholder="请输入用户标识"
          />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showNewConversationDialog = false">取消</el-button>
        <el-button
          type="primary"
          :loading="creating"
          @click="createConversation"
        >
          创建
        </el-button>
      </div>
    </el-dialog>

    <!-- 删除客户确认弹窗 -->
    <el-dialog
      title="删除客户"
      :visible.sync="showDeleteCustomerDialog"
      width="400px"
      center
    >
      <div class="delete-confirm-content">
        <i
          class="el-icon-warning"
          style="color: #f56c6c; font-size: 48px; margin-bottom: 16px"
        ></i>
        <p>
          确定要删除客户
          <strong>{{
            deletingCustomer
              ? deletingCustomer.customerName || deletingCustomer.customerId
              : ""
          }}</strong>
          吗？
        </p>
        <p style="color: #909399; font-size: 14px; margin-top: 8px">
          删除后，该客户的聊天记录将被清除，且无法恢复。
        </p>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showDeleteCustomerDialog = false">取消</el-button>
        <el-button type="danger" :loading="deleting" @click="deleteCustomer">
          删除
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { decryptAES, isBase64String } from '@/utils/crypto'

export default {
  name: "ConversationList",
  data() {
    return {
      refreshing: false,
      loading: false,
      showNewConversationDialog: false,
      creating: false,
      seletfriend: {
        customerName: "",
      },
      deleting: false, // 删除状态
      activeCollapseItems: [],
      showDeleteCustomerDialog: false, // 删除客户确认对话框
      deletingCustomer: null, // 正在删除的客户信息
      deletingAdmin: null, // 正在删除的客服信息
      // 客服状态相关
      staffStatus: "online", // online|busy
      statusChanging: false, // 防止并发状态切换
      statusLabels: {
        online: "上线",
        busy: "忙碌",
      },
      // 全局音量开关
      globalSoundEnabled: true,
      newConversationForm: {
        name: "",
        token: "",
      },
      newConversationRules: {
        name: [{ required: true, message: "请输入对话名称", trigger: "blur" }],
        token: [{ required: true, message: "请输入用户标识", trigger: "blur" }],
      },
      // 全局搜索相关
      searchType: "message", // message:历史记录 | user:用户
      globalSearchKeyword: "",
      globalSearching: false,
      showGlobalSearchResults: false,
      globalSearchResults: [],
      // 新消息提示
      hasNewMessages: false,
      // 每个客服的新消息状态
      adminNewMessagesMap: {},
      // 状态切换后的刷新定时器
      statusChangeRefreshTimer: null,
    };
  },
  computed: {
    conversations() {
      return this.$store.state.conversations;
    },
    activeConversationId() {
      return this.$store.state.activeConversationId;
    },
    // 从原始数据构建客服列表
    adminList() {
      if (!this.conversations || this.conversations.length === 0) {
        return [];
      }

      // 从 conversations 中提取原始的 admin 数据
      let admins = this.conversations;

      return admins;
    },
  },
  watch: {
    // 监听对话列表变化，检查是否有新消息或新访客
    adminList: {
      handler(newAdminList) {
        this.updateNewMessageStatus(newAdminList);
      },
      deep: true,
    },
  },
  methods: {
    // 检查某个客服是否有新消息
    getAdminHasNewMessages(admin) {
      if (!admin) return false;
      return this.adminNewMessagesMap[admin.staffId] || false;
    },
    // 更新新消息状态
    updateNewMessageStatus(adminList) {
      let hasUnread = false;
      this.adminNewMessagesMap = {};
      
      if (adminList && adminList.length > 0) {
        for (const admin of adminList) {
          let adminHasUnread = false;
          
          // 检查客服级别的未读消息
          if (admin.staffUnread && admin.staffUnread > 0) {
            adminHasUnread = true;
          }
          
          // 检查客户级别的未读消息
          if (!adminHasUnread && admin.sessions && admin.sessions.length > 0) {
            for (const session of admin.sessions) {
              if (session.staffUnread && session.staffUnread > 0) {
                adminHasUnread = true;
                break;
              }
            }
          }
          
          // 保存这个客服的新消息状态
          if (adminHasUnread) {
            this.adminNewMessagesMap[admin.staffId] = true;
            hasUnread = true;
          }
        }
      }
      
      this.hasNewMessages = hasUnread;
    },
    // 处理状态切换
    async handleStatusChange(command) {
      // 防止并发状态切换
      if (this.statusChanging) {
        console.warn("状态切换进行中，请勿重复操作");
        return;
      }
      
      if (this.staffStatus === command) return;
      
      this.statusChanging = true;
      
      try {
        const previousStatus = this.staffStatus;
        this.staffStatus = command;
        // 保存状态到localStorage
        localStorage.setItem('staffStatus', command);
        console.log('已保存客服状态到localStorage:', command);
        
        if (command === 'busy') {
          this.$message.warning("已切换为忙碌状态，消息接收和自动刷新已断开");
          
          // 清除之前的刷新定时器（如果存在）
          if (this.statusChangeRefreshTimer) {
            clearTimeout(this.statusChangeRefreshTimer);
            console.log('已清除之前的刷新定时器');
          }
          
          // 从上线切换到忙碌时，延迟 2-3 秒后刷新一次列表
          if (previousStatus === 'online') {
            const delay = 500 // 2-3 秒随机延迟
            console.log(`将在 ${delay.toFixed(0)}ms 后刷新对话列表`);
            
            this.statusChangeRefreshTimer = setTimeout(() => {
              this.refreshConversations();
              console.log('已执行状态切换后的刷新');
              this.statusChangeRefreshTimer = null;
            }, delay);
          }
        } else if (command === 'online') {
          this.$message.success("已切换为上线状态，消息接收和自动刷新已恢复");
          
          // 清除之前的刷新定时器（如果存在）
          if (this.statusChangeRefreshTimer) {
            clearTimeout(this.statusChangeRefreshTimer);
            console.log('已清除之前的刷新定时器');
          }
        }
        
        // 触发状态变更事件，通知父组件（如 Chat.vue）处理 WebSocket 连接逻辑
        // 延迟发出以确保消息显示后再处理连接
        this.$nextTick(() => {
          this.$emit("status-changed", command);
        });
      } finally {
        // 确保无论成功或失败都重置操作标志
        this.statusChanging = false;
      }
    },

    // 切换全局音量
    toggleGlobalSound() {
      this.globalSoundEnabled = !this.globalSoundEnabled;
      const status = this.globalSoundEnabled ? "已开启" : "已关闭";
      this.$message.success({
        message: `声音提醒${status}`,
        duration: 500
      });
      // 保存到 localStorage
      localStorage.setItem('globalSoundEnabled', this.globalSoundEnabled.toString());
    },

    async refreshConversations() {
      this.refreshing = true;
      try {
      
        await this.$store.dispatch("loadConversations");
        this.$message.success("刷新成功");
      } catch (error) {
        console.error("Failed to refresh conversations:", error);
        this.$message.error("刷新失败");
      } finally {
        this.refreshing = false;
      }
    },
    selectConversation(conversation) {
      console.log(conversation, "conversation");
      this.$store.commit("SET_ACTIVE_CONVERSATION", conversation.customId);
      this.$emit("conversation-selected", conversation);

      console.log(
        this.conversations,
        "conversations",
        this.activeConversationId
      );
    },
    // 切换客服展开状态
    // 清除客服所有客户的未读消息
    clearAdminUnreadCounts(adminId) {
      if (!this.conversations) return;
      this.conversations
        .filter(
          (conv) => conv.type === "user" && conv.senderAccount === adminId
        )
        .forEach((conv) => {
          if (this.$store.state.unreadCounts[conv.id]) {
            this.$store.commit("SET_UNREAD_COUNT", {
              conversationId: conv.id,
              count: 0,
            });
          }
        });
    },
    // 选择客户对话
    selectCustomerConversation(friend, admin) {
      // 检查是否处于忙碌状态
      // if (this.staffStatus === 'busy') {
      //   this.$message.warning("您当前处于忙碌状态，请切换为上线状态才能操作会话");
      //   return;
      // }
      
      localStorage.setItem("selected_session_id", friend.id);
      this.seletfriend = friend;
      // 从原始数据结构创建对话对象
      // friend 是 session 对象，admin 是 staff 对象
      const conversation = {
        ...friend,
        customId: "conv_" + admin.staffId + "_" + friend.customerId,
      };

      console.log("创建客户对话对象:", conversation);

      // 选中对话
      this.selectConversation(conversation);

      // 触发加载历史聊天记录
      // this.$emit("load-messages", conversation);
    },
    // 获取客户对话ID
    getCustomerConversationId(friend, staffId) {
      // 使用会话的ownerId作为标识符
      const sessionId = friend.customerId;
      return "conv_" + staffId + "_" + sessionId;
    },
    // 获取客服的总未读消息数
    getAdminUnreadCount(staffId) {
      if (!this.conversations) return 0;
      return this.conversations
        .filter(
          (conv) => conv.type === "user" && conv.senderAccount === staffId
        )
        .reduce((total, conv) => total + this.getUnreadCount(conv.id), 0);
    },
    getAvatarUrl() {
      return "/defaultavatar.png";
    },
    getCustomerAvatarUrl() {
      return "/defaultavatar.png";
    },
    getUnreadCount(conversationId) {
      return this.$store.getters.conversationUnreadCount(conversationId);
    },
    // 获取客户未读消息数
    getCustomerUnreadCount(friend) {
      // 新的数据结构中，未读消息数量直接在session.unreadCount中
      return friend.staffUnread || 0;
    },
    /**
     * 格式化时间戳，按规则显示：今日显时分/昨日显“昨天”/其他显月日
     * @param {number|string} timestamp - 毫秒级时间戳（支持数字/字符串类型）
     * @returns {string} 格式化后的时间字符串
     */
    formatTime(timestamp) {
      // 1. 严格的参数校验 + 类型转换
      if (!timestamp || isNaN(Number(timestamp))) return "";
      const ts = Number(timestamp); // 统一转为数字

      // 2. 处理秒级时间戳（兼容10位秒级，自动转13位毫秒级）
      const finalTs = ts.toString().length === 10 ? ts * 1000 : ts;

      const date = new Date(finalTs);
      // 校验时间戳有效性（避免 Invalid Date）
      if (date.toString() === "Invalid Date") return "";

      const now = new Date();
      // 3. 统一时间基准：都转为“年-月-日”格式，消除时分秒干扰
      const dateYmd = date.toLocaleDateString("zh-CN");
      const nowYmd = now.toLocaleDateString("zh-CN");
      // 计算昨天的日期（避免直接用时间差，解决跨天边界问题）
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayYmd = yesterday.toLocaleDateString("zh-CN");

      // 4. 核心逻辑（更精准，无跨天误差）
      // 今天：显示 时:分（补零，如 09:05 而非 9:5）
      if (dateYmd === nowYmd) {
        return date.toLocaleTimeString("zh-CN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false, // 强制24小时制，避免上午/下午
        });
      }

      // 昨天：显示“昨天”
      if (dateYmd === yesterdayYmd) {
        return "昨天";
      }

      // 其他日期：显示 月-日（补零，如 06-09）
      return date
        .toLocaleDateString("zh-CN", {
          month: "2-digit",
          day: "2-digit",
        })
        .replace(/\//g, "-"); // 替换默认的“/”为“-”，格式更统一
    },
    async createConversation() {
      try {
        await this.$refs.newConversationForm.validate();
      } catch (error) {
        return;
      }

      this.creating = true;
      try {
        // 这里应该调用创建对话的API
        // 暂时先模拟创建
        const newConversation = {
          customId: "conv_" + Date.now(),
          name: this.newConversationForm.name,
          token: this.newConversationForm.token,
          senderAccount: localStorage.getItem("login_id"),
          receiverAccount: this.newConversationForm.token,
          userAccount: this.newConversationForm.token,
          type: "user",
          lastMessage: "",
          lastTime: new Date().toISOString(),
          unreadCount: 0,
          messages: [],
        };

        this.$store.commit("ADD_CONVERSATION", newConversation);
        this.showNewConversationDialog = false;
        this.newConversationForm = { name: "", token: "" };
        this.$message.success("创建成功");
      } catch (error) {
        console.error("Failed to create conversation:", error);
        this.$message.error("创建失败");
      } finally {
        this.creating = false;
      }
    },

    // 确认删除客户
    confirmDeleteCustomer(friend, admin) {
      this.deletingCustomer = friend;
      this.deletingAdmin = admin;
      this.showDeleteCustomerDialog = true;
    },

    // 删除客户
    async deleteCustomer() {
      if (!this.deletingCustomer || !this.deletingAdmin) return;

      this.deleting = true;
      try {
        // 调用删除API
        const response = await this.$http.post(`/api/session/hide`, {
          sessionId: `${this.deletingCustomer.id}`,
        });

        if (response.data && response.data.code === 200) {
          // 从store中移除该客户会话
          this.$store.commit("REMOVE_CUSTOMER_SESSION", {
            adminId: this.deletingAdmin.staffId,
            customerId: this.deletingCustomer.customerId,
          });

          // 如果当前活动对话是正在删除的客户，清除活动对话
          const customerConversationId = this.getCustomerConversationId(
            this.deletingCustomer,
            this.deletingAdmin.id
          );
          if (this.activeConversationId === customerConversationId) {
            this.$store.commit("SET_ACTIVE_CONVERSATION", null);
          }

          this.$message.success("删除成功");
          this.showDeleteCustomerDialog = false;
        } else {
          throw new Error(response.data.msg || "删除失败");
        }
      } catch (error) {
        console.error("Failed to delete customer:", error);
        this.$message.error(error.message || "删除失败");
      } finally {
        this.deleting = false;
        this.deletingCustomer = null;
        this.deletingAdmin = null;
      }
    },

    // ========== 全局搜索相关方法 ==========
    
    // 执行全局搜索
    async performGlobalSearch() {
      const keyword = this.globalSearchKeyword.trim();
      if (!keyword) {
        this.$message.warning("请输入搜索关键词");
        return;
      }

      this.globalSearching = true;
      this.showGlobalSearchResults = true;
      this.globalSearchResults = [];

      try {
        // 根据搜索类型选择不同的API
        const apiUrl = this.searchType === 'user' ? '/api/session/search' : '/api/message/search';
        
        const response = await this.$http.get(apiUrl, {
          params: {
            keyword: keyword,
            pageNum: 1,
            pageSize: 50
          }
        });

        if (response.data && response.data.code === 200) {
          const data = isBase64String(response.data.data) 
            ? decryptAES(response.data.data) 
            : response.data.data;
          
          const records = data.records || [];
          
          if (this.searchType === 'user') {
            // 用户搜索结果
            this.globalSearchResults = records.map(record => ({
              ...record,
              displayName: record.customerName || record.customerId,
              displayContent: record.customerLocation || '用户',
              searchType: 'user'
            }));
          } else {
            // 消息搜索结果
            this.globalSearchResults = records.map(record => ({
              messageId: record.messageId,
              id: record.id,
              content: record.content,
              messageType: record.messageType,
              senderId: record.senderId,
              receiverId: record.receiverId,
              createTime: record.createTime,
              senderName: record.senderName,
              senderAvatar: record.senderAvatar,
              receiverName: record.receiverName,
              receiverAvatar: record.receiverAvatar,
              isRead: record.readStatus === 1 || record.isRead === 1,
              isRevoked: record.isRevoked === 1,
              sessionId: record.sessionId,
              customerId: record.customerId,
              staffId: record.staffId,
              staffName: record.staffName,
              customerName: record.customerName,
              displayName: record.customerName,
              displayContent: record.content,
              searchType: 'message'
            }));
          }
          
          if (this.globalSearchResults.length === 0) {
            this.$message.info("没有找到相关记录");
          }
        } else {
          throw new Error(response.data.msg || "搜索失败");
        }
      } catch (error) {
        console.error("Global search failed:", error);
        this.$message.error("搜索失败，请稍后重试");
        this.globalSearchResults = [];
      } finally {
        this.globalSearching = false;
      }
    },

    // 清除全局搜索
    clearGlobalSearch() {
      this.globalSearchKeyword = "";
      this.showGlobalSearchResults = false;
      this.globalSearchResults = [];
    },

    // 高亮关键词
    highlightKeyword(content, keyword) {
      if (!content || !keyword) return content;
      
      // 转义正则特殊字符
      const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedKeyword})`, 'gi');
      
      return content.replace(regex, '<span class="highlight-keyword">$1</span>');
    },

    // 通过 sessionId 从后端拉取会话对象
    async fetchSessionById(sessionId) {
      try {
        const response = await this.$http.get('/api/session', {
          params: { sessionId }
        });
        if (response.data && response.data.code === 200) {
          const data = isBase64String(response.data.data)
            ? decryptAES(response.data.data)
            : response.data.data;
          return data || null;
        }
      } catch (e) {
        console.error('fetchSessionById error:', e);
      }
      return null;
    },

    // 通过Vuex将会话插入到对应客服下（若不存在则新增/替换），返回 { admin, session }
    upsertSessionIntoAdminList(session) {
      if (!session) return { admin: null, session: null };
      this.$store.commit('UPSERT_CUSTOMER_SESSION', session);
      const staffId = session.staffId || session.ownerStaffId || session.ownerId;
      const admin = this.$store.state.conversations.find(a => a && a.staffId == staffId);
      if (!admin || !admin.sessions) return { admin: null, session: null };
      const s = admin.sessions.find(x => x && x.id == session.id);
      return { admin, session: s || session };
    },

    // 跳转到搜索结果对应的对话
    async jumpToSearchResult(result) {
      // 检查是否处于忙碌状态
      // if (this.staffStatus === 'busy') {
      //   this.$message.warning("您当前处于忙碌状态，请切换为上线状态才能操作会话");
      //   return;
      // }

      console.log("跳转到搜索结果:", result);
      
      // 如果是用户搜索结果
      if (this.searchType === 'user') {
        // 遍历所有客服，查找该用户的会话
        let foundSession = null;
        let foundAdmin = null;

        for (const admin of this.adminList) {
          if (admin.sessions && admin.sessions.length > 0) {
            const session = admin.sessions.find(s => s.customerId == result.customerId);
            if (session) {
              foundSession = session;
              foundAdmin = admin;
              break;
            }
          }
        }

        // 本地找不到则尝试后端拉取（当返回结果里有sessionId可用时）
        if (!foundSession || !foundAdmin) {
          const fallbackSessionId = result && (result.sessionId || result.id);
          if (fallbackSessionId) {
            const remoteSession = await this.fetchSessionById(fallbackSessionId);
            if (remoteSession) {
              const inserted = this.upsertSessionIntoAdminList(remoteSession);
              foundAdmin = inserted.admin;
              foundSession = inserted.session;
            }
          }
        }

        if (!foundSession || !foundAdmin) {
          this.$message.warning("未找到该用户的对话");
          return;
        }

        // 展开对应的客服折叠面板
        if (!this.activeCollapseItems.includes(foundAdmin.staffId)) {
          this.activeCollapseItems.push(foundAdmin.staffId);
        }

        // 选中对应的客户对话
        this.selectCustomerConversation(foundSession, foundAdmin);

        // 关闭搜索结果面板
        this.showGlobalSearchResults = false;
      } else {
        // 消息搜索结果 - 原有逻辑
        const sessionId = result.sessionId;
        
        if (!sessionId) {
          this.$message.warning("搜索结果缺少必要信息");
          return;
        }

        // 遍历所有客服，查找包含该 sessionId 的会话
        let foundSession = null;
        let foundAdmin = null;

        for (const admin of this.adminList) {
          if (admin.sessions && admin.sessions.length > 0) {
            const session = admin.sessions.find(s => s.id == sessionId);
            if (session) {
              foundSession = session;
              foundAdmin = admin;
              break;
            }
          }
        }

        // 本地找不到则调用接口获取并插入
        if (!foundSession || !foundAdmin) {
          const remoteSession = await this.fetchSessionById(sessionId);
          if (remoteSession) {
            const inserted = this.upsertSessionIntoAdminList(remoteSession);
            foundAdmin = inserted.admin;
            foundSession = inserted.session;
          }
        }

        if (!foundSession || !foundAdmin) {
          this.$message.warning("未找到对应的聊天记录");
          return;
        }

        // 展开对应的客服折叠面板
        if (!this.activeCollapseItems.includes(foundAdmin.staffId)) {
          this.activeCollapseItems.push(foundAdmin.staffId);
        }

        // 选中对应的客户对话
        this.selectCustomerConversation(foundSession, foundAdmin);

        // 关闭搜索结果面板
        this.showGlobalSearchResults = false;

        // 触发消息定位事件（通知 ChatArea 定位到该消息）
        this.$emit("locate-message", {
          messageId: result.messageId,
          keyword: this.globalSearchKeyword
        });
      }
    },
  },
  mounted() {
    // 从localStorage加载全局音量设置
    const savedSoundEnabled = localStorage.getItem('globalSoundEnabled');
    if (savedSoundEnabled !== null) {
      this.globalSoundEnabled = savedSoundEnabled === 'true';
    }

    // 从localStorage恢复客服状态
    const savedStaffStatus = localStorage.getItem('staffStatus');
    if (savedStaffStatus && (savedStaffStatus === 'online' || savedStaffStatus === 'busy')) {
      this.staffStatus = savedStaffStatus;
      console.log('已恢复客服状态:', this.staffStatus);
    } else {
      console.log('未找到保存的状态，使用默认状态: online');
    }
    
    // 无论如何都发出初始状态事件，确保建立正确的 WebSocket 连接
    // 延迟发出，让父组件完全初始化后再处理
    setTimeout(() => {
      console.log('页面初始化完成，发出客服状态事件:', this.staffStatus);
      this.$emit("status-changed", this.staffStatus);
    }, 200);
  },
  beforeDestroy() {
    // 清除定时器，防止内存泄漏
    if (this.statusChangeRefreshTimer) {
      clearTimeout(this.statusChangeRefreshTimer);
      console.log('组件卸载时已清除刷新定时器');
      this.statusChangeRefreshTimer = null;
    }
  },
};
</script>


<style scoped>

:deep(.el-collapse-item__header.is-active) {
  color: #409eff;         /* 激活时的文字颜色 */
  background-color: #f5f7fa; /* 激活时的背景色 */
  font-weight: bold;      /* 字体加粗 */
  border-bottom-color: transparent; /* 去除展开时的下边框 */
}

/* }  */
.conversation-list {
  width: 320px;
  background-color: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.conversation-list-header {
  padding: 15px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
}

.conversation-list-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* NEW 内联徽章样式 */
.new-badge-inline {
  font-size: 9px;
  font-weight: bold;
  color: #f56c6c;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
  padding: 0px 8px;
  border-radius: 8px;
  border: 1px solid #f56c6c;
  animation: blink 1.2s ease-in-out infinite;
  white-space: nowrap;
  font-family: 'Arial', sans-serif;
  letter-spacing: 0.3px;
  line-height: 30px;
  display: inline-block;
}

.admin-unread-section {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
@keyframes blink {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

.new-badge-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.new-badge {
  font-size: 12px;
  font-weight: bold;
  color: #f56c6c;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
  padding: 4px 8px;
  border-radius: 12px;
  border: 1px solid #f56c6c;
  animation: blink 1.2s ease-in-out infinite;
  white-space: nowrap;
  font-family: 'Arial Black', sans-serif;
  letter-spacing: 1px;
}

/* 头部状态下拉按钮 */
.status-dropdown-btn {
  padding: 6px 10px !important;
  font-size: 13px !important;
  display: flex !important;
  align-items: center;
  gap: 6px;
  background-color: transparent !important;
  border-color: #dcdfe6 !important;
  color: #606266 !important;
}

.status-dropdown-btn:hover {
  background-color: #f5f7fa !important;
  border-color: #c0c4cc !important;
}

.status-label {
  margin: 0 4px;
}

/* 头部筛选器下拉菜单 */
.filter-dropdown-header :deep(.el-button) {
  padding: 6px 12px;
  font-size: 12px;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid #409eff;
  color: #409eff;
}

.filter-dropdown-header :deep(.el-button:not(.is-disabled)):hover {
  /* background-color: #f0f7ff; */
  opacity: 0.9;
}

.filter-menu-header {
  min-width: 160px !important;
}

.filter-menu-header :deep(.el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  font-size: 13px;
  padding: 4px 16px;
  height: auto;
  line-height: 1.4;
  white-space: nowrap;
}

.filter-menu-header :deep(.el-dropdown-menu__item i) {
  margin-right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  font-style: normal;
  font-weight: normal;
}

.filter-label-text {
  display: inline-block;
}

/* 头部筛选器容器 */
.filter-dropdown-header {
  display: inline-block;
}

/* 全局音量按钮 */
.sound-button {
  padding: 6px 12px !important;
  font-size: 16px !important;
}

.sound-button:hover {
  background-color: #f0f7ff !important;
}

.volume-icon {
  font-style: normal;
}

.volume-icon.icon-volume::before {
  content: "🔊";
  display: inline-block;
}

.volume-icon.icon-volume-mute::before {
  content: "🔇";
  display: inline-block;
}

/* 筛选图标样式 */
.filter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #999;
  flex-shrink: 0;
}

.filter-icon.online-dot {
  background-color: #cdfa75;
}

.filter-icon.offline-dot {
  background-color: #f48324;
}

.filter-icon.busy-dot {
  background-color: #ffa500;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
}

/* 下拉菜单中的勾选和空白图标 */
.filter-menu-header :deep(.check-icon) {
  color: #409eff;
  font-weight: bold;
}

.filter-menu-header :deep(.el-icon-blank) {
  opacity: 0;
  width: 16px;
}

.filter-menu-header :deep(.filter-label-text) {
  margin-left: 4px;
}

.conversation-items {
  flex: 1;
  overflow-y: auto;
  padding:12px;
  background-color: #ccc;
}

/* 全局搜索样式 */
.global-search-container {
  padding: 12px 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.search-type-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0;
}

.search-type-label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
  font-weight: 500;
}

.search-type-radio-group {
  flex-shrink: 0;
}

.search-type-radio-group :deep(.el-radio-button__inner) {
  padding: 6px 12px;
  font-size: 12px;
  height: auto;
  line-height: 1;
}

.search-type-radio-group :deep(.el-radio-button) {
  margin-right: 0;
}

.search-input-row {
  display: flex;
  width: 100%;
}

.search-input-row :deep(.el-input) {
  width: 100%;
}

.global-search-container :deep(.el-input-group__append) {
  background-color: #409eff;
  border-color: #409eff;
  color: white;
}

.global-search-container :deep(.el-input-group__append:hover) {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.global-search-results {
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  max-height: 300px;
  overflow-y: auto;
  flex-shrink: 0;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-size: 13px;
  color: #606266;
  position: sticky;
  top: 0;
  z-index: 1;
}

.search-loading {
  padding: 20px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

.search-loading i {
  margin-right: 8px;
}

.no-search-results {
  padding: 20px;
}

.search-results-list {
  padding: 8px 0;
}

.search-result-item {
  padding: 12px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.search-result-item:hover {
  background-color: #f5f7fa;
}

.search-result-item:last-child {
  border-bottom: none;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.result-customer-name {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
}

.result-time {
  font-size: 12px;
  color: #909399;
}

.result-user-id {
  font-size: 12px;
  color: #909399;
}

.result-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-content span {
  display: block;
}

.result-content :deep(.highlight-keyword) {
  background-color: #ffd54f;
  color: #333;
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 500;
}

.result-staff {
  font-size: 12px;
  color: #909399;
}

/* 客服组 */
.admin-group {
  border-bottom: 1px solid #f0f0f0;
}

/* 客服项 */
.admin-item {
  background-color: #fafafa;
  padding-left: 20px;
  position: relative;
}

.admin-item.expanded {
  background-color: #f5f5f5;
}

.admin-indicator {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background-color: #e6a23c;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

/* 客户列表 */
.customer-list {
  background-color: #ffffff;
}

.customer-item {
  padding-left: 40px;
  border-bottom: 1px solid #f8f8f8;
  position: relative;
}

.customer-item:hover {
  background-color: #f9f9f9;
}

.customer-item.active {
  background-color: #f0f7ff;
  border-right: 3px solid #409eff;
}

.customer-actions {
  margin-left: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.customer-item:hover .customer-actions {
  opacity: 1;
}

.delete-customer-btn {
  color: #f56c6c;
  padding: 4px;
}

.delete-customer-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}

.customer-badge {
  background: #67c23a;
  color: white;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 2px;
  margin-left: 6px;
  flex-shrink: 0;
}

.conversation-item {
  position: relative;
  padding: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
}

.conversation-avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 10px;
  flex-shrink: 0;
}

.conversation-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.conversation-info {
  flex: 1;
  min-width: 0;
}

.conversation-name {
  font-weight: 500;
  margin-bottom: 4px;
  font-size: 15px;
  display: flex;
 
  align-items: center;
  min-width: 0; /* 允许子元素缩小 */
}


.conversation-name > *:not(.online-status-tag) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0; /* 允许缩小 */
}

.online-status-tag {
  margin-left: 8px;
  flex-shrink: 0;
  font-size: 11px;
}

.admin-badge {
  background: #e6a23c;
  color: white;
  font-size: 10px;
  border-radius: 2px;
  margin-left: 6px;
  /* flex-shrink: 0;  */
}

.conversation-last-msg {
  color: #999;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.conversation-time {
  color: #999;
  font-size: 12px;
}

.unread-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: #f56c6c;
  color: white;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  border: 2px solid white;
  box-sizing: border-box;
}

.admin-unread-badge {
  position: relative;
  background-color: #f56c6c;
  color: white;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  border: 2px solid white;
  box-sizing: border-box;
  padding: 0 6px;
  flex-shrink: 0;
}

.no-conversations {
  padding: 40px 20px;
}

/* 手风琴折叠面板样式 */
.conversation-accordion {
  border: none;
}

.conversation-accordion .el-collapse-item__header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0;
  height: auto;
  min-height: 60px;
  border-radius: 4px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.conversation-accordion .el-collapse-item__header:hover {
  background-color: #e9ecef;
}

.conversation-accordion .el-collapse-item__arrow {
  margin: 0 10px 0 0;
  color: #666;
  transition: transform 0.3s ease;
}

.conversation-accordion .el-collapse-item__wrap {
  border-bottom: none;
}

.conversation-accordion .el-collapse-item__content {
  padding: 0;
  border-radius: 0 0 4px 4px;
}

.accordion-header {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 15px;
  position: relative;
  cursor: pointer;
  gap: 12px;
}

/* 客服信息容器 */
.staff-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.staff-name {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
}

.customer-count-tag {
  flex-shrink: 0;
  font-size: 11px !important;
  padding: 3px 8px !important;
}

/* 声音按钮 */
.sound-toggle {
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.sound-toggle:hover {
  transform: scale(1.15);
}

.admin-collapse-item{
  margin-top: 6px;
}
.admin-collapse-item .accordion-header {
  cursor: pointer;
}

.customer-list {
  background-color: #fafafa;
  border-radius: 0 0 4px 4px;
}

.customer-list .conversation-item {
  margin: 0;
  border-radius: 0;
  border-bottom: 1px solid #e9ecef;
}

.customer-list .conversation-item:last-child {
  border-bottom: none;
  border-radius: 0 0 4px 4px;
}

.no-customers {
  padding: 20px;
  background-color: #fafafa;
  border-radius: 0 0 4px 4px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .conversation-list {
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1000;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  .conversation-list.show {
    transform: translateX(0);
  }

  .conversation-list-header {
    padding: 12px;
  }

  .conversation-list-header h2 {
    font-size: 16px;
  }

  .conversation-item {
    padding: 12px;
  }

  .admin-item {
    padding-left: 15px;
  }

  .customer-item {
    padding-left: 30px;
  }

  .online-status-tag {
    font-size: 10px;
    margin-left: 4px;
  }
}

@media (max-width: 480px) {
  .conversation-avatar {
    width: 35px;
    height: 35px;
  }

  .conversation-name {
    font-size: 14px;
  }

  .conversation-last-msg {
    font-size: 12px;
  }

  .conversation-time {
    font-size: 11px;
  }

  .online-status-tag {
    font-size: 9px;
  }
}

/* 删除确认对话框样式 */
.delete-confirm-content {
  text-align: center;
  padding: 20px 0;
}

.delete-confirm-content p {
  margin: 0;
  line-height: 1.5;
}
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.25),    
 }

.status-dot.online {
  background-color: rgb(205, 249, 117);
}

.status-dot.offline {
  background-color: rgb(244, 131, 36);
}

</style>
